// Third party dependencies
import {
  Box,
  Heading,
  Line,
  MainArea,
  Page,
  P,
  BottomBar,
  Gap,
  SaveBar,
  Error as ErrorComponent,
} from '@salecycle/ui-component-library';

// Local dependencies
import { UnsavedChangesModal } from 'core/modals/UnsavedChangesModal';
import FileExportTransformations from '../components/FileExportTransformations';
import FileExportTransformPreview from '../components/FileExportTransformPreview';
import { FileExportFolderWithCopy } from '../components/FileExportFolderWithCopy';
import { AppTopBar } from 'core/ui/components/AppTopBar/AppTopBar';
import { FileManagementRoute } from '../routes';
import { useHistory } from 'react-router';
import { useState } from 'react';
import { ClientLevel } from 'core/types';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { useTranslation } from 'react-i18next';

export const FileExportConfigPage = ({
  s3SubFolder,
  saveEnabled,
  clientId,
  timestampFormat,
  timestampRemoval,
  compressFile,
  onDiscardChanges,
  clientLevel,
  clientName,
  folderName = '',
  showError = false,
  error = null,
  setTimestampRemoval,
  setTimestampFormat,
  setCompressFile,
  setS3SubFolder,
  mutate,
}) => {
  const { push } = useHistory();
  const { t } = useTranslation('fileExport');
  const { showNotification } = useNotifications();

  const [unsavedChangesModalIsOpen, setUnsavedChangesModalIsOpen] = useState(false);

  const gotoFileExportPage = () => {
    setUnsavedChangesModalIsOpen(false);
    push(FileManagementRoute.FILE_EXPORT, { clientId, clientLevel: clientLevel ?? ClientLevel.SITE });
  };

  const handleExit = () => {
    !!s3SubFolder ? setUnsavedChangesModalIsOpen(true) : gotoFileExportPage();
  };

  const handleSave = () => {
    const config = {
      folder: s3SubFolder,
      transformations: {
        timestampFormat: { active: timestampFormat },
        timestampRemoval: { active: timestampRemoval },
        compression: { active: compressFile },
      },
    };
    mutate(config, {
      onSuccess: data => {
        gotoFileExportPage();
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(t('config.create.saveError'), 'error');
      },
    });
  };

  const toggleTransformation = (transformation: string, checked: boolean) => {
    switch (transformation) {
      case 'timestampRemoval':
        setTimestampRemoval(checked);
        break;
      case 'timestampFormat':
        setTimestampFormat(checked);
        break;
      case 'compressFile':
        setCompressFile(checked);
        break;
    }
  };

  const renderError = () =>
    showError && (
      <div>
        <ErrorComponent heading={t('error.heading')} description={error?.message || t('error.description')} />
      </div>
    );

  const renderTransformations = () => {
    if (showError) {
      return null;
    }

    return (
      <>
        <FileExportTransformations
          clientId={clientId}
          timestampRemoval={timestampRemoval}
          timestampFormat={timestampFormat}
          compressFile={compressFile}
          s3SubFolder={s3SubFolder}
          onToggle={toggleTransformation}
        />
        <Line gapTop={30} />
        <FileExportTransformPreview
          compressFile={compressFile}
          timestampFormat={timestampFormat}
          timestampRemoval={timestampRemoval}
        />
      </>
    );
  };

  return (
    <MainArea>
      <AppTopBar />
      <Page>
        <>
          {!!s3SubFolder && (
            <UnsavedChangesModal
              heading={t('config.create.unsavedChangesModal.heading')}
              isOpen={unsavedChangesModalIsOpen}
              onOkButtonClicked={gotoFileExportPage}
              onCancelButtonClicked={() => setUnsavedChangesModalIsOpen(false)}
            >
              <P>{t('config.create.unsavedChangesModal.text1')}</P>
              <Gap top={10} />
              <P>{t('config.create.unsavedChangesModal.text2')}</P>
            </UnsavedChangesModal>
          )}
          <Box stackItems={true}>
            <Heading>{t('config.create.heading')}</Heading>
            <P>{t('config.create.description', { clientName })}</P>
            <Gap top={20} />
            <FileExportFolderWithCopy
              clientId={clientId}
              onChangeFolder={setS3SubFolder}
              s3SubFolder={s3SubFolder}
              isDisabled={!!folderName}
            />
            <Line gapTop={0} />
            <>
              {renderError()}
              {renderTransformations()}
            </>
          </Box>
        </>
      </Page>
      <BottomBar>
        <SaveBar
          saveButtonLabel={t('config.create.saveButton')}
          onExitButtonClicked={handleExit}
          onDiscardButtonClicked={onDiscardChanges}
          onSaveButtonClicked={handleSave}
          enableSaveButton={saveEnabled}
        />
      </BottomBar>
    </MainArea>
  );
};
